'use client';

import type { GettingStartedStepKey } from '@/lib/services/onboarding/types';
import { trackGuidanceEvent } from './track';

/**
 * Emits `guidance-getting-started-step-completed` when a step becomes complete
 * while the user is watching.
 *
 * **Why the browser reports this at all, when the server already knows.**
 * VALE-1830 records completion server-side, off the events the real work already
 * emits, and that record is the authoritative count: a `preference` field a
 * `scripts/db.sh` query reads per step. What it cannot do is reach Intercom: the
 * Intercom event has to be sent on the identified user, and the user is
 * identified in the browser by the Messenger. A server-side Intercom REST call
 * would be the only one in the codebase and would need a new access-token
 * secret, for a count we already hold. So the browser reports the transition it
 * observes, and the server trace remains the number anyone should query.
 *
 * **It undercounts, deliberately, and never overcounts.** The first snapshot of
 * a session establishes a baseline and emits nothing, so steps already complete
 * at sign-in are not re-reported. A completion the browser never refetches after
 * is missed. Both are one-directional: a step is reported at most once per
 * session and only when this browser saw it change, which is the honest thing a
 * client can say. Anyone comparing the Intercom count against the server count
 * should expect the server's to be higher, and the server's is the right one.
 *
 * **Module-level state on purpose.** Three surfaces read the same progress hook
 * (the hub, the sidebar widget and the icon rail: the rail is mounted on every
 * page), so per-instance state would emit the same completion up to three times.
 * One baseline and one reported set, shared by every caller, makes it once.
 */

/** Steps seen complete on the first snapshot: the baseline, never reported. */
let baseline: ReadonlySet<GettingStartedStepKey> | null = null;

/** Steps already reported this session, so a refetch cannot report them twice. */
const reported = new Set<GettingStartedStepKey>();

/**
 * Observe the current completed-step set and report anything newly complete.
 *
 * Call it with every snapshot the progress hook resolves, including the first:
 * the first is what establishes the baseline. Safe to call repeatedly with an
 * unchanged set; it does nothing.
 */
export function observeGettingStartedCompletions(
  completed: ReadonlySet<GettingStartedStepKey>
): void {
  if (baseline === null) {
    baseline = new Set(completed);
    return;
  }

  // `forEach` rather than `for…of`: the tsconfig target predates iterable Set
  // downlevelling, and a spread into an array would allocate on every snapshot
  // of a hook that runs on every page.
  const seen = baseline;
  completed.forEach((step) => {
    if (seen.has(step) || reported.has(step)) return;
    reported.add(step);
    trackGuidanceEvent('guidance-getting-started-step-completed', { step });
  });
}

/**
 * Clears the session state. **Tests only**: the module-level baseline is what
 * makes the once-per-session guarantee hold across three mounted surfaces, so
 * nothing in the product should reset it.
 */
export function resetGettingStartedCompletionsForTest(): void {
  baseline = null;
  reported.clear();
}
